/**
 * In-memory pub/sub store for server-computed order KPI stats.
 *
 * Written by the data provider on each orders fetch.
 * Consumed by HUDContext via useSyncExternalStore.
 *
 * Same pattern as storeIdsStore.ts — simple module-level state with listeners.
 */

export interface OrderKPIStats {
  waitingToPack: number;
  packed: number;
  inTransit: number;
  deliveredToday: number;
  /** Today's deliveries that took over an hour from placed to delivered. */
  completedOverHourToday?: number;
}

/** One "Needs a look" card, bucketed server-side over the whole pipeline. */
export interface NeedsALookCard {
  count: number;
  orderIds: string[];
  oldestMinutes: number | null;
  oldestStore: string | null;
}

export type NeedsALookKey =
  | 'storeNotAccepted'
  | 'unpacked'
  | 'courierUnassigned'
  | 'notCollected'
  | 'deliveryNotCompleted';

export type NeedsALook = Record<NeedsALookKey, NeedsALookCard>;

let stats: OrderKPIStats = {
  waitingToPack: 0,
  packed: 0,
  inTransit: 0,
  deliveredToday: 0,
  completedOverHourToday: 0,
};

let needsALook: NeedsALook | null = null;

const listeners = new Set<() => void>();

export const setOrderStats = (newStats: OrderKPIStats) => {
  stats = newStats;
  listeners.forEach((l) => l());
};

export const getOrderStats = () => stats;

export const setNeedsALook = (next: NeedsALook | null) => {
  needsALook = next;
  listeners.forEach((l) => l());
};

export const getNeedsALook = () => needsALook;

export const subscribeOrderStats = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
